import * as React from 'react';
import { findDOMNode } from 'react-dom';
import cn from 'classnames';
import Slider, { Handle, HandleProps } from 'rc-slider';
import Tooltip from 'rc-tooltip';
import 'rc-slider/assets/index.css';
import { Field, FieldProps } from 'formik';

const styles = require('./index.scss');

interface SliderHandleProps extends HandleProps {
    showHandleText: boolean;
    value: number;
    containerRef: Element;
    dragging: any;
    index: any;
    step?: number;
}

const handle: React.FC<SliderHandleProps> = (props) => {
    // Need to pop out all the extra props before passing then down to Handle component or we'll
    // get annoying console warnings
    const { step, dragging, index, showHandleText, containerRef, value, ...restProps } = props;

    return (
        <div>
            <Tooltip
                key={index}
                prefixCls={cn(styles.sliderTooltip)}
                overlay={`${value}`}
                visible={true}
                placement='top'
                getTooltipContainer={props.containerRef ? () => findDOMNode(props.containerRef) as Element : undefined}
            >
                <Handle {...restProps} />
            </Tooltip>
        </div>
    );
};

type SliderSettingsProps = {
    min: number;
    max: number;
    defaultValue: number;
    onChange: (value: number) => void;
    step?: number;
    value?: number;
};

type SliderFieldProps = {
    sliderProps: SliderSettingsProps;
    containerRef: HTMLDivElement | null;
    fieldProps: FieldProps;
};

const SliderField: React.FC<SliderFieldProps> = ({ sliderProps, containerRef }) => {
    const { min = 0, max = 60, defaultValue = 0, step = 1, value } = sliderProps;

    const conditionalProps: { value?: number } = {};
    if (value) {
        conditionalProps.value = value;
    }
    return (
        <Slider
            min={min}
            max={max}
            defaultValue={value !== undefined ? value : defaultValue}
            className={cn(styles.slider)}
            step={step}
            value={value}
            handle={(handleProps: React.PropsWithChildren<SliderHandleProps>) => {
                return handle({
                    ...handleProps,
                    containerRef: containerRef as Element,
                    ...conditionalProps,
                });
            }}
            onChange={(newValue: number) => {
                sliderProps.onChange(newValue);
            }}
            {...conditionalProps}
        />
    );
};

type OneWaySliderProps = {
    formikName: string;
} & SliderSettingsProps;

export const OneWaySlider: React.FC<OneWaySliderProps> = ({ formikName, ...restProps }) => {
    const [ref, setRef] = React.useState<HTMLDivElement | null>(null);

    return (
        <div className={styles.container} ref={setRef}>
            {ref && (
                <Field name={formikName}>
                    {(fieldProps: FieldProps) => {
                        return <SliderField sliderProps={restProps} containerRef={ref} fieldProps={fieldProps} />;
                    }}
                </Field>
            )}
        </div>
    );
};
